import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, MenuList } from '@material-ui/core';
import cn from 'classnames';
import colors from '../../foundations/colors.module.scss';
import { openInNewTabProps } from '../../utils/externalLinks';
import { getTestIds } from '../../utils/TestIds';
import { useStateInRef } from '../../utils/useStateInRef';
import { Badge } from '../Badge';
import { useBiAction } from '../BiTracking';
import { useIsInBottomSheet } from '../BottomSheet';
import { CircularLoader } from '../CircularLoader';
import { Column, Row } from '../Layout';
import { MenuItem } from '../MenuItem';
import { Searchbox } from '../Searchbox';
import { TextButton } from '../TextButton';
import { Typography } from '../Typography';
import styles from './Menu.module.scss';
import { MenuFooter } from './MenuFooter';
const useMenuListStyles = makeStyles({
    root: {
        paddingTop: 4,
        flex: 1,
        overflow: 'auto',
        outline: 'none',
        touchAction: 'pan-y'
    },
    sectionHeader: {
        '&.Mui-disabled': {
            opacity: 1,
            color: "".concat(colors.dark700, " !important"),
            margin: '0 1px'
        }
    },
    item: {
        '&.Mui-disabled': {
            '& *': {
                color: 'inherit !important'
            }
        }
    }
});
export const GetMenuTID = (parentTestId)=>getTestIds(parentTestId);
function keepOnlyHeadersWithItems(items) {
    const result = [];
    for(let i = 0; i < items.length; i++){
        const item = items[i];
        if (!item.isSectionHeader) {
            result.push(item);
            continue;
        }
        const nextItem = items[i + 1];
        if (nextItem && !nextItem.isSectionHeader) {
            result.push(item);
        }
    }
    return result;
}
export function Menu(props) {
    var _ref;
    var _action_getError;
    const { testId, loading, searchProps, footer, items, emptyText, dimensions, classes, menuListRef, flat } = props;
    const menuListClasses = useMenuListStyles();
    const isInBottomSheet = useIsInBottomSheet();
    const footerItems = (footer === null || footer === void 0 ? void 0 : footer.items) || [];
    const { showSearch, strategy, autoFocus, placeholder: searchPlaceholder, bi, noBi, action } = searchProps || {};
    const logBiAction = useBiAction();
    const { height = 'auto', width = 400, minHeight = 80, maxHeight = 500, minWidth, maxWidth, paddingBottom, paddingTop } = dimensions || {};
    const { MenuTID } = useMemo(()=>GetMenuTID(testId), [
        testId
    ]);
    const [search, setSearch] = useState('');
    const getStrategy = useStateInRef(strategy);
    const [autoFocusItem, setAutoFocusItem] = useState(false);
    const scrollContainerRef = useRef(null);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const updateScrollIndicator = useCallback(()=>{
        const el = scrollContainerRef.current;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const overflows = scrollHeight > clientHeight;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
        setShowScrollIndicator(overflows && !atBottom);
    }, []);
    const scrollCheckTimeoutRef = useRef(null);
    const setMenuListRef = useCallback((el)=>{
        scrollContainerRef.current = el;
        if (menuListRef) {
            menuListRef.current = el;
        }
        if (scrollCheckTimeoutRef.current) {
            clearTimeout(scrollCheckTimeoutRef.current);
            scrollCheckTimeoutRef.current = null;
        }
        if (el) {
            requestAnimationFrame(()=>updateScrollIndicator());
            scrollCheckTimeoutRef.current = setTimeout(updateScrollIndicator, 150);
        }
    }, [
        menuListRef,
        updateScrollIndicator
    ]);
    const filteredItems = useMemo(()=>{
        const strategy = getStrategy();
        if (!showSearch || !strategy) {
            return items;
        }
        const filtered = items.filter((item)=>{
            if (item.isSectionHeader) {
                return true;
            }
            switch(strategy.type){
                case 'text':
                    {
                        const trimmedSearch = search.trim().toLowerCase();
                        return typeof item.title === 'string' && item.title.toLowerCase().includes(trimmedSearch);
                    }
                case 'custom':
                    return strategy.filterItem(search, item);
            }
        });
        return keepOnlyHeadersWithItems(filtered);
    }, [
        items,
        showSearch,
        search
    ]);
    useEffect(()=>{
        updateScrollIndicator();
    }, [
        updateScrollIndicator,
        filteredItems
    ]);
    const trimmedSearch = search.trim();
    const showAction = action != null && action.shouldShow(trimmedSearch);
    const actionError = showAction ? (_action_getError = action.getError) === null || _action_getError === void 0 ? void 0 : _action_getError.call(action, trimmedSearch) : undefined;
    const actionTestId = (_ref = action === null || action === void 0 ? void 0 : action.testId) !== null && _ref !== void 0 ? _ref : MenuTID.Action();
    const triggerAction = useCallback(()=>{
        var _action_bi;
        if (!showAction || actionError != null) return;
        if ((_action_bi = action.bi) === null || _action_bi === void 0 ? void 0 : _action_bi.name) {
            logBiAction({
                entityType: 'button',
                entityName: action.bi.name,
                actionType: 'click',
                testId: actionTestId,
                data: action.bi.data
            });
        }
        action.onSubmit(trimmedSearch);
    }, [
        action,
        showAction,
        actionError,
        trimmedSearch,
        actionTestId,
        logBiAction
    ]);
    useEffect(()=>{
        if (autoFocusItem) {
            setAutoFocusItem(false);
        }
    }, [
        autoFocusItem
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.root, classes === null || classes === void 0 ? void 0 : classes.root, {
            [styles.inBottomSheet]: isInBottomSheet,
            [styles.flat]: flat
        }),
        "data-testid": MenuTID.Root(),
        style: _object_spread_props(_object_spread({}, isInBottomSheet ? {
            maxHeight: 'none',
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%'
        } : {
            height,
            maxHeight,
            width,
            minWidth,
            maxWidth
        }), {
            minHeight,
            paddingBottom,
            paddingTop
        }),
        children: [
            showSearch && /*#__PURE__*/ _jsxs("div", {
                className: styles.searchSection,
                children: [
                    /*#__PURE__*/ _jsx(Searchbox, _object_spread_props(_object_spread({}, {
                        bi,
                        noBi
                    }), {
                        variant: "textfield",
                        fluidWidth: true,
                        autoFocus: autoFocus,
                        placeholder: searchPlaceholder,
                        testId: MenuTID.Search(),
                        value: search,
                        onChange: (e)=>setSearch(e.target.value),
                        onClear: ()=>setSearch(''),
                        onKeyDown: (e)=>{
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setAutoFocusItem(true);
                                return;
                            }
                            if (e.key === 'Enter') {
                                const firstSelectableItem = filteredItems.find((item)=>!item.isSectionHeader && !item.disabled);
                                if (firstSelectableItem != null) {
                                    var _firstSelectableItem_onClick;
                                    e.preventDefault();
                                    (_firstSelectableItem_onClick = firstSelectableItem.onClick) === null || _firstSelectableItem_onClick === void 0 ? void 0 : _firstSelectableItem_onClick.call(firstSelectableItem);
                                    return;
                                }
                                if (showAction) {
                                    e.preventDefault();
                                    triggerAction();
                                }
                            }
                        }
                    })),
                    showAction && /*#__PURE__*/ _jsx(TextButton, {
                        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- Menu self-logs this action in triggerAction with action.bi; a button bi here would double-fire
                        noBi: true,
                        weight: "normal",
                        variant: "paragraph2",
                        underline: "none",
                        suffixIcon: action.suffixIcon,
                        onClick: triggerAction,
                        disabled: actionError != null,
                        testId: actionTestId,
                        children: action.label
                    })
                ]
            }),
            actionError != null && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                skin: "error",
                className: styles.actionError,
                testId: MenuTID.ActionError(),
                children: actionError
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.listWrapper,
                children: [
                    /*#__PURE__*/ _jsx(MenuList, {
                        disablePadding: true,
                        autoFocusItem: autoFocusItem,
                        classes: {
                            root: cn(menuListClasses.root, classes === null || classes === void 0 ? void 0 : classes.menuList)
                        },
                        ref: setMenuListRef,
                        onScroll: updateScrollIndicator,
                        children: loading ? /*#__PURE__*/ _jsx("li", {
                            className: styles.loading,
                            children: /*#__PURE__*/ _jsx(CircularLoader, {
                                testId: MenuTID.Loading()
                            })
                        }) : filteredItems.length > 0 ? filteredItems.map((item, index)=>{
                            var _item_testId;
                            if (item.isSectionHeader) {
                                var _item_testId1;
                                return /*#__PURE__*/ _jsx(MenuItem, {
                                    disabled: true,
                                    className: styles.sectionHeader,
                                    classes: {
                                        root: menuListClasses.sectionHeader
                                    },
                                    testId: (_item_testId1 = item.testId) !== null && _item_testId1 !== void 0 ? _item_testId1 : item.id,
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph1",
                                        weight: "normal",
                                        children: item.label
                                    })
                                }, item.id);
                            }
                            return /*#__PURE__*/ _jsx(MenuItem, {
                                variant: "multiline",
                                testId: (_item_testId = item.testId) !== null && _item_testId !== void 0 ? _item_testId : MenuTID.Item(index),
                                className: cn(styles.item, menuListClasses.item),
                                onClick: ()=>{
                                    var _item_bi, _item_onClick;
                                    if (item.disabled) {
                                        return;
                                    }
                                    if ((_item_bi = item.bi) === null || _item_bi === void 0 ? void 0 : _item_bi.name) {
                                        var _item_testId;
                                        logBiAction({
                                            entityType: 'menu_item',
                                            entityName: item.bi.name,
                                            actionType: 'select',
                                            testId: (_item_testId = item.testId) !== null && _item_testId !== void 0 ? _item_testId : MenuTID.Item(index),
                                            data: item.bi.data
                                        });
                                    }
                                    (_item_onClick = item.onClick) === null || _item_onClick === void 0 ? void 0 : _item_onClick.call(item);
                                },
                                disabled: item.disabled,
                                intent: item.intent,
                                selected: item.selected,
                                style: item.tooltip && item.disabled ? {
                                    pointerEvents: 'auto'
                                } : undefined,
                                tooltipProps: {
                                    title: item.tooltip || '',
                                    placement: 'top-start',
                                    centerArrow: true
                                },
                                children: /*#__PURE__*/ _jsxs(Row, _object_spread_props(_object_spread({
                                    alignItems: "center",
                                    gap: 8
                                }, item.externalLink ? _object_spread({
                                    as: 'a',
                                    href: item.externalLink.href
                                }, item.externalLink.openInNewTab ? openInNewTabProps : {}) : {}), {
                                    children: [
                                        item.prefixIcon && /*#__PURE__*/ _jsx("div", {
                                            className: styles.noShrink,
                                            children: item.prefixIcon
                                        }),
                                        /*#__PURE__*/ _jsxs(Column, {
                                            width: "100%",
                                            className: styles.itemContentColumn,
                                            children: [
                                                item.title && /*#__PURE__*/ _jsxs(Row, {
                                                    gap: 4,
                                                    alignItems: "center",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraph1",
                                                            ellipsis: true,
                                                            weight: item.lightTitle ? 'light' : 'normal',
                                                            skin: "secondary",
                                                            className: styles.changeTextColorOnHover,
                                                            testId: MenuTID.ItemTitle(index),
                                                            children: item.title
                                                        }),
                                                        item.badge && /*#__PURE__*/ _jsx(Badge, {
                                                            outlined: true,
                                                            rounded: true,
                                                            skin: "primary",
                                                            testId: MenuTID.ItemBadge(index),
                                                            children: item.badge.text
                                                        })
                                                    ]
                                                }),
                                                item.body && /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "paragraph1",
                                                    skin: "secondary",
                                                    weight: "light",
                                                    className: styles.changeTextColorOnHover,
                                                    testId: MenuTID.ItemBody(index),
                                                    children: item.body
                                                }),
                                                item.subtext && /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "paragraph3",
                                                    skin: "secondary",
                                                    className: styles.changeTextColorOnHover,
                                                    testId: MenuTID.ItemSubtext(index),
                                                    children: item.subtext
                                                })
                                            ]
                                        }),
                                        item.suffixIcon && /*#__PURE__*/ _jsx("div", {
                                            className: styles.noShrink,
                                            children: item.suffixIcon
                                        })
                                    ]
                                }))
                            }, item.id);
                        }) : /*#__PURE__*/ _jsx("li", {
                            className: styles.emptyText,
                            "data-testid": MenuTID.Empty(),
                            children: /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                skin: "disabled",
                                children: emptyText
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.scrollIndicator, {
                            [styles.hidden]: !showScrollIndicator
                        }),
                        "aria-hidden": true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(MenuFooter, {
                items: footerItems,
                className: footer === null || footer === void 0 ? void 0 : footer.className
            })
        ]
    });
}
